import React, { useMemo, useState } from 'react';
import { useDraggable } from '@dnd-kit/core';
import { functionRegistry } from '../../utils/functionRegistry';
import type { FunctionDefinition } from '../../utils/functionRegistry';

const FUNCTION_GROUP_ORDER = [
  'Aggregation',
  'Momentum',
  'Date & Time',
  'Trend',
  'Patterns',
  'Gaps',
  'Pivot Points',
];

type DraggableFunctionProps = {
  id: string;
  label: string;
  setDraggedItem: (item: { type: string; label: string; functionId?: string } | null) => void;
  /** Use 'operator' for larger hit area in the operator palette */
  variant?: 'default' | 'operator';
};

export const DraggableFunction: React.FC<DraggableFunctionProps> = ({ id, label, setDraggedItem, variant = 'default' }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id,
    data: { type: 'function', functionId: id },
  });

  const sizeClass = variant === 'operator' ? 'px-4 py-2 min-w-[4rem]' : 'px-2 py-1';

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onMouseDown={() => setDraggedItem({ type: 'function', label, functionId: id })}
      onMouseUp={() => setDraggedItem(null)}
      className={`cursor-move rounded-sm border border-rule text-sm ${sizeClass} ${isDragging ? 'opacity-50' : 'bg-newsprint-dark hover:bg-newsprint'}`}
    >
      {label}
    </div>
  );
};

type AvailableFunctionsProps = {
  setDraggedItem: (item: { type: string; label: string; functionId?: string } | null) => void;
};

function groupFunctionsByGroup(): Map<string, [string, FunctionDefinition][]> {
  const map = new Map<string, [string, FunctionDefinition][]>();
  for (const [id, def] of functionRegistry.entries()) {
    if (def.category !== 'function') continue;
    const group = def.group;
    if (!map.has(group)) map.set(group, []);
    map.get(group)!.push([id, def]);
  }
  return map;
}

function orderedGroupNames(grouped: Map<string, [string, FunctionDefinition][]>): string[] {
  const names = Array.from(grouped.keys());
  return names.sort((a, b) => {
    const ai = FUNCTION_GROUP_ORDER.indexOf(a);
    const bi = FUNCTION_GROUP_ORDER.indexOf(b);
    return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
  });
}

const AvailableFunctions: React.FC<AvailableFunctionsProps> = ({ setDraggedItem }) => {
  const grouped = useMemo(groupFunctionsByGroup, []);
  const groupNames = useMemo(() => orderedGroupNames(grouped), [grouped]);

  const [openGroups, setOpenGroups] = useState<Set<string>>(() => new Set(['Patterns']));

  const toggleGroup = (group: string) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(group)) next.delete(group);
      else next.add(group);
      return next;
    });
  };

  return (
    <div>
      <h3 className="font-bold mb-2">Functions</h3>
      <div className="flex flex-col gap-1">
        {groupNames.map((groupName) => {
          const entries = grouped.get(groupName) ?? [];
          const isOpen = openGroups.has(groupName);
          return (
            <div key={groupName} className="flex flex-col gap-1">
              <button
                type="button"
                onClick={() => toggleGroup(groupName)}
                className="flex items-center gap-1 w-full text-left text-sm font-semibold py-1 px-0 rounded-sm hover:bg-newsprint cursor-pointer border-none bg-transparent text-ink"
                aria-expanded={isOpen}
              >
                <span className="text-ink/80 w-4 shrink-0" aria-hidden>
                  {isOpen ? '\u25BC' : '\u25B6'}
                </span>
                <span>{groupName}</span>
                <span className="text-ink/60 text-xs font-normal">({entries.length})</span>
              </button>
              {isOpen && (
                <div className="flex flex-col gap-1 pl-4">
                  {entries.map(([id, def]) => (
                    <DraggableFunction key={id} id={id} label={def.label} setDraggedItem={setDraggedItem} />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AvailableFunctions;
